import { api, unwrap, toPaginated } from "../lib/axios";
import { API_BASE_URL } from "../config/env";
import { tokenStore } from "../lib/tokenStore";
import type { ApiEnvelope, PaginatedResult } from "../types/api";
import type {
  CommissionRecord,
  DriverBalanceSummary,
  DriverPayout,
  LatraMonthlyReport,
  TaxReceipt,
} from "../types/domain";

export type SettlementDirection = "payment" | "payout";
export type SettlementChannel = "cash" | "bank" | "mobile-money" | "offset";

export interface ManualSettlementBody {
  driverID: string;
  amount: number;
  direction: SettlementDirection;
  channel: SettlementChannel;
  reference?: string;
  note?: string;
  attachmentUrl?: string;
}

export interface ManualSettlementResult {
  transactionId: string;
  platformDebt: number;
  collection: {
    allocatedRecordIds: string[];
    allocatedTotal: number;
    remainder: number;
    receiptQueued: boolean;
  } | null;
}

export interface DriverCommissionResult {
  records: CommissionRecord[];
  balance: DriverBalanceSummary | null;
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const settlementsApi = {
  /**
   * Record a payment made outside the payment gateway. Posts immediately —
   * there is no approval step — and, for an inbound payment, allocates against
   * pending commission which triggers the fiscal receipt.
   */
  async recordManual(
    body: ManualSettlementBody,
  ): Promise<ManualSettlementResult> {
    const res = await api.post<ApiEnvelope<ManualSettlementResult>>(
      "/admin/settlements/manual",
      body,
    );
    return unwrap(res);
  },

  async driverCommission(
    driverId: string,
    params: { page?: number; limit?: number; status?: string } = {},
  ): Promise<DriverCommissionResult> {
    const res = await api.get<ApiEnvelope<DriverCommissionResult>>(
      `/admin/drivers/${driverId}/commission`,
      { params },
    );
    return unwrap(res);
  },

  /** Send null to clear the override and revert to the global default. */
  async setDebtLimit(
    driverId: string,
    maxDriverDebtOverride: number | null,
  ): Promise<{ driverID: string; maxDriverDebtOverride: number | null }> {
    const res = await api.patch<
      ApiEnvelope<{ driverID: string; maxDriverDebtOverride: number | null }>
    >(`/admin/drivers/${driverId}/debt-limit`, { maxDriverDebtOverride });
    return unwrap(res);
  },
};

export const taxReceiptsApi = {
  async list(
    params: {
      page?: number;
      limit?: number;
      status?: string;
      driverId?: string;
      search?: string;
    } = {},
  ): Promise<PaginatedResult<TaxReceipt>> {
    const res = await api.get<ApiEnvelope<unknown>>("/tax-receipts", { params });
    return toPaginated<TaxReceipt>(res, "receipts");
  },

  async status(): Promise<{
    enabled: boolean;
    configured: boolean;
    trips: Record<string, number>;
    receipts: Record<string, number>;
  }> {
    const res = await api.get<ApiEnvelope<any>>("/tax-receipts/status");
    return unwrap(res);
  },

  /** `ref` is the collection event key: "trip:<id>" or "txn:<id>". */
  async reissue(ref: string): Promise<void> {
    await api.post(`/tax-receipts/collection/${encodeURIComponent(ref)}/reissue`);
  },
};

export const latraReportApi = {
  /** Omit `month` for the previous calendar month (EAT). */
  async monthly(month?: string): Promise<LatraMonthlyReport> {
    const res = await api.get<ApiEnvelope<LatraMonthlyReport>>(
      "/latra/monthly-report",
      { params: month ? { month } : {} },
    );
    return unwrap(res);
  },

  /**
   * Download the month's report. A plain <a download> can't carry the Bearer
   * header, so fetch with auth → Blob → synthetic anchor (same approach as
   * reportsApi.downloadCsv).
   */
  async download(month: string, format: "csv" | "pdf"): Promise<void> {
    const url = `${API_BASE_URL}/latra/monthly-report/export?month=${month}&format=${format}`;

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${tokenStore.getAccess() ?? ""}` },
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);

    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `safiri-latra-${month}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },

  async remittances(limit = 24): Promise<any[]> {
    const res = await api.get<ApiEnvelope<any[]>>("/latra/remittances", {
      params: { limit },
    });
    return unwrap(res);
  },

  async markFiled(period: string, referenceNumber: string): Promise<any> {
    const res = await api.post<ApiEnvelope<any>>(
      `/latra/monthly-report/${period}/file`,
      { referenceNumber },
    );
    return unwrap(res);
  },
};

// ---------------------------------------------------------------------------
// Driver cash-outs (gateway-executed disbursements)
// ---------------------------------------------------------------------------

export type PayoutStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";

export interface PayoutStatusSummary {
  /** False means no cash-out can be executed at all. */
  enabled: boolean;
  /** Which credential is missing, when `enabled` is false. */
  configurationError: string | null;
  payouts: Record<string, number>;
}

export const payoutsApi = {
  /**
   * Gateway-executed cash-outs only.
   *
   * Admin-recorded payouts are transactions with category `manualPayout` and
   * live under /admin/transactions — they never appear here.
   */
  async list(
    params: {
      page?: number;
      limit?: number;
      status?: string;
      driverID?: string;
    } = {},
  ): Promise<PaginatedResult<DriverPayout>> {
    const res = await api.get<ApiEnvelope<unknown>>("/admin/payouts", {
      params,
    });
    return toPaginated<DriverPayout>(res);
  },

  async status(): Promise<PayoutStatusSummary> {
    const res = await api.get<ApiEnvelope<PayoutStatusSummary>>(
      "/admin/payouts/status",
    );
    return unwrap(res);
  },

  /**
   * Re-query one stuck payout against the gateway.
   *
   * Never re-sends the transfer — it only asks what happened to the reference
   * already held, so it is safe to press repeatedly.
   */
  async reconcile(payoutId: string): Promise<void> {
    await api.post(`/admin/payouts/${payoutId}/reconcile`);
  },
};
